<template>
  <button
    :type="type"
    :disabled="disabled"
    :style="buttonStyle"
    :class="buttonClasses"
    :title="title"
    @mousedown="handleMouseDown"
    @mouseup="handleMouseUp"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
    @touchstart="handleTouchStart"
    @touchend="handleTouchEnd"
    @click="$emit('click', $event)"
  >
    <span class="relative overflow-hidden w-full h-full flex items-center justify-center">
      <span :class="contentSpanClasses" :style="contentSpanStyle">
        <PContent v-if="isStringSlot" className="text-center text-white font-bold">
          {{ slotContent }}
        </PContent>
        <div v-else class="text-white font-bold text-center flex items-center justify-center gap-2">
          <slot />
        </div>
      </span>
      <span :class="duplicateSpanClasses" :style="duplicateSpanStyle">
        <PContent v-if="isStringSlot" className="text-center text-white font-bold">
          {{ slotContent }}
        </PContent>
        <div v-else class="text-white font-bold text-center flex items-center justify-center gap-2">
          <slot />
        </div>
      </span>
    </span>
  </button>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, useSlots, Text } from 'vue'
import PContent from '../text/PContent.vue'

interface Props {
  className?: string;
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
  heigthRatio?: number;
  title?: string;
}

const props = withDefaults(defineProps<Props>(), {
  className: '',
  type: 'button',
  disabled: false,
  heigthRatio: 0.23,
  title: ''
})

const emit = defineEmits<{
  click: [event: MouseEvent]
}>()

const slots = useSlots()
const slotContent = ref<string>('')
const isStringSlot = computed(() => {
  if (!slots.default) return false
  const defaultSlot = slots.default()
  if (defaultSlot.length === 1) {
    const child = defaultSlot[0]
    if (typeof child.children === 'string') {
      slotContent.value = child.children
      return true
    }
    if (child.type === Text && typeof child.children === 'string') {
      slotContent.value = child.children
      return true
    }
  }
  return false
})

const height = ref(50)
const width = ref(120)
const pressed = ref(false)
const isHovered = ref(false)
const isMobile = ref(false)

const updateSize = () => {
  const screenWidth = window.innerWidth
  const WidthMobileM = 120
  const ScreenWidthMobileM = 320
  const WidthKscreen = 233
  const ScreenWidthKscreen = 2560

  const a = (WidthKscreen - WidthMobileM) / (ScreenWidthKscreen - ScreenWidthMobileM)
  const b = WidthMobileM - a * ScreenWidthMobileM

  const widthRef = a * screenWidth + b
  const calculatedHeight = 0.30 * widthRef
  
  width.value = widthRef
  height.value = calculatedHeight

  isMobile.value = screenWidth < 1024
}

onMounted(() => {
  updateSize()
  window.addEventListener('resize', updateSize)
})

onUnmounted(() => {
  window.removeEventListener('resize', updateSize)
})

const radiusRatio = 30 / 66
const borderRadius = computed(() => height.value * radiusRatio)

const normalShadow = `inset 0px -2px 6px rgba(0, 0, 0, 0.1), 
  inset -2px 0px 4px rgba(255, 255, 255, 0.3), 
  inset 2px 0px 4px rgba(0, 0, 0, 0.3), 
  inset 0px -6px 4px rgba(0, 0, 0, 0.1), 
  inset 0px 6px 4px rgba(255, 255, 255, 0.3)`

const pressedShadow = `inset 0px -1px 3px rgba(0, 0, 0, 0.1), 
  inset -1px 0px 2px rgba(255, 255, 255, 0.3), 
  inset 1px 0px 2px rgba(0, 0, 0, 0.3), 
  inset 0px -3px 2px rgba(0, 0, 0, 0.1), 
  inset 0px 3px 2px rgba(255, 255, 255, 0.3)`

const getScale = computed(() => {
  if (pressed.value) return 'scale(0.90)'
  if (isHovered.value && !isMobile.value) return 'scale(1.05)'
  return 'scale(1)'
})

const handleMouseDown = () => {
  pressed.value = true
}

const handleMouseUp = () => {
  pressed.value = false
}

const handleMouseEnter = () => {
  isHovered.value = true
}

const handleMouseLeave = () => {
  pressed.value = false
  isHovered.value = false
}

const handleTouchStart = () => {
  if (!isMobile.value) return
  pressed.value = true
}

const handleTouchEnd = () => {
  if (!isMobile.value) return
  pressed.value = false
}

const buttonStyle = computed(() => ({
  width: `${width.value}px`,
  height: `${height.value}px`,
  borderRadius: `${borderRadius.value}px`,
  boxShadow: pressed.value ? pressedShadow : normalShadow,
  transform: getScale.value,
  transition: 'box-shadow 0.2s ease, transform 0.3s ease-out, border-radius 0.2s ease, opacity 0.2s ease',
  overflow: 'hidden',
  WebkitUserSelect: 'none' as const,
  MozUserSelect: 'none' as const,
  msUserSelect: 'none' as const,
  userSelect: 'none' as const,
  WebkitTouchCallout: 'none' as const,
  WebkitTapHighlightColor: 'transparent',
  opacity: props.disabled ? 0.5 : 1,
  cursor: props.disabled ? 'not-allowed' : 'pointer'
}))

const buttonClasses = computed(() => {
  const classes = ['bg-primary', 'px-7', 'group', 'relative', props.className].filter(Boolean)
  return classes.join(' ')
})

const contentSpanClasses = computed(() => {
  return [
    'flex items-center justify-center transition-transform duration-300 w-full h-full',
    !isMobile.value ? 'group-hover:translate-x-full' : ''
  ].join(' ')
})

const contentSpanStyle = computed(() => {
  if (isMobile.value) {
    return {
      transform: pressed.value ? 'translateX(100%)' : 'translateX(0)'
    }
  }
  return {}
})

const duplicateSpanClasses = computed(() => {
  return [
    'flex items-center justify-center absolute top-0 left-0 w-full h-full transition-transform duration-300',
    !isMobile.value ? 'group-hover:translate-x-[100%]' : ''
  ].join(' ')
})

const duplicateSpanStyle = computed(() => {
  const baseStyle = { left: '-100%' }
  if (isMobile.value) {
    return {
      ...baseStyle,
      transform: pressed.value ? 'translateX(100%)' : 'translateX(0)'
    }
  }
  return baseStyle
})
</script>

<style scoped>
button {
  cursor: pointer;
}
</style>
